import { Tooltip } from '@mui/material';

import { AlertCircle, AlertTriangle, ArrowRight, CheckCircle2, Info, Loader2 } from 'lucide-react';

import { orangeError } from '../../../theme.ts';
import type { NextStep, StepAction, StepMessage, StepTone } from './types';

const TONE: Record<StepTone, { box: string; text: string; icon: JSX.Element }> = {
  error: {
    box: 'bg-red-500/10',
    text: 'text-red-200',
    icon: <AlertCircle size={14} className="text-red-400" />,
  },
  warning: {
    box: 'bg-amber-400/10',
    text: 'text-amber-100',
    icon: <AlertTriangle size={14} className="text-amber-400" />,
  },
  info: { box: '', text: 'text-gray-300', icon: <Info size={14} className="text-gray-400" /> },
  progress: { box: '', text: 'text-gray-200', icon: <Loader2 size={14} className="text-lime-400 animate-spin" /> },
  success: { box: '', text: 'text-lime-200', icon: <CheckCircle2 size={14} className="text-lime-400" /> },
  next: {
    box: 'bg-lime-400/10 ring-1 ring-inset ring-lime-400/30',
    text: 'text-lime-100',
    icon: <ArrowRight size={14} className="text-lime-400" />,
  },
};

function Message({ message }: { message: StepMessage }) {
  const tone = TONE[message.tone];
  return (
    <div
      role={message.tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2 rounded-md text-[12px] leading-[17px] ${tone.box ? `${tone.box} px-2 py-1.5` : 'px-0.5'}`}
    >
      <span className="mt-[1.5px] shrink-0">{tone.icon}</span>
      <p className={`min-w-0 ${tone.text}`}>
        {message.text}
        {message.link && (
          <>
            {' '}
            <button
              type="button"
              onClick={message.link.onClick}
              className="font-medium text-white underline underline-offset-2 decoration-white/40 hover:decoration-white"
            >
              {message.link.label}
            </button>
          </>
        )}
      </p>
    </div>
  );
}

const VARIANT = {
  primary:
    'bg-lime-400 text-black hover:bg-lime-300 disabled:bg-white/10 disabled:text-gray-400 disabled:hover:bg-white/10',
  secondary:
    'border border-white/15 text-gray-100 hover:bg-white/5 hover:border-white/25 disabled:hover:bg-transparent',
  danger: 'text-white hover:brightness-110',
};

function ActionButton({ action, grow }: { action: StepAction; grow: boolean }) {
  const variant = action.variant ?? (grow ? 'primary' : 'secondary');
  const iconOnly = !!action.iconOnly && !grow;
  const button = (
    <button
      type={action.formId ? 'submit' : 'button'}
      form={action.formId}
      onClick={action.onClick}
      disabled={action.disabled}
      aria-label={iconOnly ? action.label : undefined}
      style={variant === 'danger' ? { backgroundColor: orangeError } : undefined}
      className={`h-9 rounded-full text-[13px] font-medium flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:cursor-not-allowed ${
        iconOnly ? 'w-9' : 'w-full px-3.5'
      } ${VARIANT[variant]} ${action.disabled && variant !== 'primary' ? 'opacity-50' : ''}`}
    >
      {action.loading ? <Loader2 size={16} className="animate-spin" /> : action.icon}
      {!iconOnly && <span className="truncate">{action.label}</span>}
    </button>
  );
  // The span keeps the tooltip working on a disabled button.
  return (
    <Tooltip title={action.tooltip ?? (iconOnly ? action.label : '')} placement="top" arrow>
      <span className={grow ? 'flex-1 min-w-0' : 'shrink-0'}>{button}</span>
    </Tooltip>
  );
}

/** The panel's fixed bottom: what is wrong or missing, then the next step's buttons. */
export function PanelFooter({ step }: { step: NextStep }) {
  const { messages, content, primary, secondary } = step;
  if (messages.length === 0 && !content && !primary && !secondary) return null;
  return (
    <div className="shrink-0 border-t border-white/10 bg-[#1b1b1b] px-3 pt-2.5 pb-3 space-y-2.5">
      {messages.length > 0 && (
        <div className="space-y-1.5 max-h-[132px] overflow-y-auto workspace-panel-scroll">
          {messages.map((message) => (
            <Message key={message.id} message={message} />
          ))}
        </div>
      )}
      {content}
      {(primary || secondary) && (
        <div className="flex items-center gap-2">
          {secondary && <ActionButton action={secondary} grow={!primary} />}
          {primary && <ActionButton action={primary} grow />}
        </div>
      )}
    </div>
  );
}
